import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/api/queryKeys'
import { fetchSession, googleSignInUrl, logout, type SessionState } from '@/api/auth'
import { isNative } from '@/native'

export function useSession() {
  return useQuery({
    queryKey: queryKeys.session(),
    queryFn: fetchSession,
    staleTime: Infinity,
    retry: 1,
  })
}

export function useGoogleSignIn() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      if (!isNative) {
        // Full-page redirect; the server sends the browser back to / signed in.
        window.location.assign(googleSignInUrl())
        return new Promise<never>(() => {})
      }
      const { nativeGoogleSignIn } = await import('@/native/googleSignIn')
      await nativeGoogleSignIn()
    },
    onSuccess: async () => {
      // Drop anything fetched while signed out, then let the app mount fresh.
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'session' })
      await qc.invalidateQueries({ queryKey: queryKeys.session() })
    },
  })
}

export function useLogout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: logout,
    onSettled: () => {
      qc.removeQueries({ predicate: (query) => query.queryKey[0] !== 'session' })
      qc.setQueryData<SessionState>(queryKeys.session(), { authenticated: false })
    },
  })
}
